/**
 * Campus Selector Component
 * Allows users to filter data by campus
 */

import React from "react";
import {
  Select,
  MenuItem,
  FormControl,
  Box,
  Typography,
  CircularProgress,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { School as SchoolIcon } from "@mui/icons-material";
import { useUIStore } from "@/store/uiStore";
import { useCampusNameMap, useCampuses } from "@/hooks/useCampuses";
import { toast } from "react-hot-toast";
import { useSearchParams } from "react-router-dom";

export const CampusSelector: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { selectedCampusId, setSelectedCampusId } = useUIStore();
  const { data: campusesData, isLoading } = useCampuses({ status: "active" });
  const [searchParams, setSearchParams] = useSearchParams();

  // Sync URL params with store on mount only
  React.useEffect(() => {
    const campusParam = searchParams.get("campus");
    if (campusParam && campusParam !== selectedCampusId) {
      setSelectedCampusId(campusParam === "all" ? null : campusParam);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Update URL when campus selection changes
  const handleCampusChange = (campusId: string | null) => {
    setSelectedCampusId(campusId);

    // Update URL params
    const newParams = new URLSearchParams(searchParams);
    if (campusId === null) {
      newParams.set("campus", "all");
    } else {
      newParams.set("campus", campusId);
    }
    setSearchParams(newParams, { replace: true });
  };

  const campuses = campusesData?.campuses || [];
  const selectedCampus = campuses.find((c) => c.campus_id === selectedCampusId);

  // A remembered campus that is no longer active (archived, or a stale
  // ?campus= link) would filter every page while the picker says "All
  // Campuses"; and with one active campus left the picker is hidden, so a
  // remembered campus couldn't be cleared. Either way: back to all
  // campuses, so what the header says is what applies -- and say so.
  const loaded = !!campusesData && Array.isArray(campusesData.campuses);
  const stale = !!selectedCampusId && loaded && (!selectedCampus || campuses.length <= 1);
  const allNames = useCampusNameMap();
  React.useEffect(() => {
    if (!stale || !selectedCampusId) return;
    const name = allNames.get(selectedCampusId);
    setSelectedCampusId(null);
    // The URL only when it names a campus (no needless navigation).
    if (searchParams.has("campus")) {
      const next = new URLSearchParams(searchParams);
      next.set("campus", "all");
      setSearchParams(next, { replace: true });
    }
    if (campuses.length > 1) {
      toast(`${name ?? "That campus"} is no longer an active campus, so all campuses are shown.`, { id: "campus-reset" });
    }
    // Only when it turns stale.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stale]);

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", alignItems: "center", px: 2 }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  // Don't show selector if there's only one or no campuses
  if (campuses.length <= 1) {
    return null;
  }

  return (
    <FormControl size="small" sx={{ minWidth: { xs: 100, sm: 180 } }}>
      <Select
        value={selectedCampusId || "all"}
        onChange={(e) =>
          handleCampusChange(e.target.value === "all" ? null : e.target.value)
        }
        // Its name for screen readers and voice control. It says the choice
        // too: on a phone the field shows only an icon.
        inputProps={{ "aria-label": `Campus: ${selectedCampus?.name ?? "All Campuses"}` }}
        displayEmpty
        renderValue={(value) => {
          if (value === "all" || !value) {
            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <SchoolIcon fontSize="small" />
                {!isMobile && (
                  <Typography variant="body2" sx={{
                    fontWeight: 500
                  }}>
                    All Campuses
                  </Typography>
                )}
              </Box>
            );
          }

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SchoolIcon fontSize="small" />
              {!isMobile && (
                <Typography variant="body2" noWrap sx={{
                  fontWeight: 500
                }}>
                  {selectedCampus?.name || "All Campuses"}
                </Typography>
              )}
            </Box>
          );
        }}
        sx={{
          "& .MuiSelect-select": {
            display: "flex",
            alignItems: "center",
            py: 1,
            minWidth: { xs: "auto", sm: 120 },
          },
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: "divider",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "primary.main",
          },
        }}
      >
        <MenuItem value="all">
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <SchoolIcon fontSize="small" color="action" />
            <Box>
              <Typography variant="body2" sx={{
                fontWeight: 600
              }}>
                All Campuses
              </Typography>
              <Typography variant="caption" sx={{
                color: "text.secondary"
              }}>
                View data for entire school
              </Typography>
            </Box>
          </Box>
        </MenuItem>

        {campuses.map((campus) => (
          <MenuItem key={campus.campus_id} value={campus.campus_id}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <SchoolIcon fontSize="small" color="primary" />
              <Box>
                <Typography variant="body2" sx={{
                  fontWeight: 600
                }}>
                  {campus.name}
                </Typography>
                <Typography variant="caption" sx={{
                  color: "text.secondary"
                }}>
                  {campus.code}
                </Typography>
              </Box>
            </Box>
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
